import { useState } from "react";
import { Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Checkbox } from "../../components/ui/checkbox";
import { useLang } from "../../lib/i18n";
import { useAuth } from "../../lib/auth";
import { api, errMsg, eur, fmtDate, rateOf } from "../../lib/api";
import { SailorDialog } from "../account/Profile";
import { useFetch } from "../../lib/useFetch";
import { classesOf } from "../../components/site/Img";
import { DateInput } from "../../components/site/DateInput";
import { cartChanged } from "./Cart";

const days = (a, b) => Math.round((new Date(b) - new Date(a)) / 86400000) + 1;

export const EnrollDialog = ({ e, onClose, onDone }) => {
  const { t, tr, lang } = useLang();
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [added, setAdded] = useState(null);
  const [done, setDone] = useState([]);
  const [newKid, setNewKid] = useState(false);
  const parts = user.participants || [];
  const evClasses = classesOf(e);
  const race = e.race?.name ? e.race : null;
  const sailOf = (p, cls) => ((p?.boats || []).find((b) => b.class_name === cls) || {}).sail_number || "";
  const cls0 = evClasses.length === 1 ? evClasses[0] : "";
  const [f, setF] = useState({ participant_id: parts.length === 1 ? parts[0].id : "", class_name: cls0, boat_option: "own", charter_id: e.charters?.[0]?.id || null, own_boat_class: cls0, own_sail_number: parts.length === 1 ? sailOf(parts[0], cls0) : "", services: [], notes: "", participation_start: e.start_date, participation_end: e.end_date, join_race: false });
  const [busy, setBusy] = useState(false);
  const pickClass = (c) => setF({ ...f, class_name: c, own_boat_class: c, own_sail_number: sailOf(parts.find((p) => p.id === f.participant_id), c) });
  const datesOk = f.participation_start >= e.start_date && f.participation_end <= e.end_date && f.participation_end >= f.participation_start;

  const clinic = e.fee * (1 + (e.tax_rate || 0) / 100);
  const ch = f.boat_option === "charter" ? e.charters.find((c) => c.id === f.charter_id) : null;
  const charter = ch ? (ch.price * (ch.price_unit === "day" ? days(f.participation_start, f.participation_end) : 1) + (ch.extra_costs_amount || 0)) * (1 + rateOf(ch, e.tax_rate) / 100) : 0;
  const services = (e.services || []).filter((s) => f.services.includes(s.name)).reduce((a, s) => a + Number(s.price) * (1 + rateOf(s, e.tax_rate) / 100), 0);

  const submit = async (goCart) => {
    setBusy(true);
    try {
      await api.post("/me/cart/items", { event_id: e.id, ...f });
      toast.success(t("cart_added"));
      cartChanged();
      onDone?.();
      const p = parts.find((x) => x.id === f.participant_id);
      if (p?.relation === "child") { setDone([...done, p.id]); setAdded(p); return; }
      if (goCart) nav("/cart"); else onClose();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const Opt = ({ v, label }) => (
    <button type="button" onClick={() => setF({ ...f, boat_option: v })} data-testid={`enroll-boat-${v}`}
      className={`flex-1 rounded-2xl border-2 p-4 text-left font-semibold transition-colors ${f.boat_option === v ? "border-[#FF5722] bg-orange-50 text-[#0A2540]" : "border-slate-200 text-slate-500"}`}>{label}</button>
  );

  const pick = (p) => { setF({ ...f, participant_id: p.id, own_sail_number: sailOf(p, f.class_name), services: [], notes: "" }); setAdded(null); };
  const kidSaved = async (p) => { setNewKid(false); await refresh(); if (p?.id) pick(p); };
  const canAddKid = user.role !== "admin";
  const { data: cart } = useFetch(added ? "/me/cart" : null);
  const { data: regs } = useFetch(added ? "/me/registrations" : null);
  const taken = new Set([...(cart?.items || []), ...(regs || []).filter((r) => r.status !== "cancelled")].filter((x) => x.event_id === e.id).map((x) => x.participant_id));
  const others = parts.filter((p) => p.relation === "child" && !done.includes(p.id) && !taken.has(p.id));
  const NewKidBtn = () => canAddKid && (
    <button type="button" onClick={() => setNewKid(true)} className="inline-flex items-center gap-1 rounded-full border-2 border-dashed border-[#00A8B5] px-4 py-2 text-sm font-semibold text-[#00A8B5]" data-testid="enroll-new-child"><Plus size={14} />{t("enroll_new_child")}</button>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && !newKid && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto rounded-3xl" data-testid="enroll-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{tr(e, "title")}</DialogTitle></DialogHeader>
        {newKid && <SailorDialog initial={{ relation: "child", last_name: user.last_name, address: user.address, country: user.country }} onClose={() => setNewKid(false)} onSaved={kidSaved} />}
        {added ? (
          <div className="space-y-5" data-testid="enroll-another-prompt">
            <p className="rounded-2xl bg-emerald-50 p-4 text-sm text-emerald-800" data-testid="enroll-added-for">{t("enroll_added_for").replace("{name}", `${added.first_name} ${added.last_name}`)}</p>
            <p className="font-semibold text-[#0A2540]">{t("enroll_another_q")}</p>
            <div className="flex flex-wrap gap-2">
              {others.map((p) => <button type="button" key={p.id} onClick={() => pick(p)} className="rounded-full border-2 border-slate-200 px-4 py-2 text-sm font-semibold text-[#0A2540] hover:border-[#00A8B5]" data-testid={`enroll-another-${p.id}`}>{p.first_name} {p.last_name}</button>)}
              <NewKidBtn />
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <button onClick={onClose} className="flex-1 rounded-full border-2 border-[#0A2540] px-5 py-3 text-sm font-semibold text-[#0A2540]" data-testid="enroll-another-no">{t("enroll_no_close")}</button>
              <button onClick={() => nav("/cart")} className="btn-buoy flex-1 justify-center" data-testid="enroll-another-cart">{t("enroll_no_cart")}</button>
            </div>
          </div>
        ) : (
        <div className="space-y-5">
          {(parts.length > 1 || canAddKid) && (
            <div className="space-y-2"><p className="text-sm font-semibold">{t("select_sailor")}</p>
              <div className="flex flex-wrap gap-2">{parts.map((p) => (
                <button type="button" key={p.id} onClick={() => pick(p)} data-testid={`enroll-participant-${p.id}`}
                  className={`rounded-full border-2 px-4 py-2 text-sm font-semibold ${f.participant_id === p.id ? "border-[#00A8B5] bg-cyan-50 text-[#0A2540]" : "border-slate-200 text-slate-500"}`}>{p.first_name} {p.last_name}</button>))}
                <NewKidBtn />
              </div>
            </div>
          )}
          {evClasses.length > 1 && (
            <div className="space-y-2"><p className="text-sm font-semibold">{t("choose_class")}</p>
              <div className="flex flex-wrap gap-2" data-testid="enroll-class-picker">{evClasses.map((c) => (
                <button type="button" key={c} onClick={() => pickClass(c)} data-testid={`enroll-class-${c.replace(/ /g, "-")}`}
                  className={`rounded-full border-2 px-4 py-2 text-sm font-semibold ${f.class_name === c ? "border-[#00A8B5] bg-cyan-50 text-[#0A2540]" : "border-slate-200 text-slate-500"}`}>{c}</button>))}
              </div>
            </div>
          )}
          <div className="space-y-2" data-testid="enroll-dates">
            <p className="text-sm font-semibold">{t("participation_dates")}</p>
            <div className="flex gap-2">
              <DateInput min={e.start_date} max={e.end_date} value={f.participation_start} onChange={(v) => setF({ ...f, participation_start: v })} data-testid="enroll-date-start" />
              <DateInput min={f.participation_start || e.start_date} max={e.end_date} value={f.participation_end} onChange={(v) => setF({ ...f, participation_end: v })} data-testid="enroll-date-end" />
            </div>
            {!datesOk && <p className="text-xs text-rose-600" data-testid="enroll-dates-error">{t("dates_within_event")}</p>}
          </div>
          {race && (
            <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-slate-200 p-4" data-testid="enroll-race-option">
              <Checkbox checked={f.join_race} onCheckedChange={(v) => setF({ ...f, join_race: !!v, participation_end: v && race.end_date && race.end_date <= e.end_date && f.participation_end < race.end_date ? race.end_date : f.participation_end })} data-testid="enroll-join-race" />
              <span className="text-sm"><b>{t("join_race")}: {tr(e, "race.name")}</b><span className="block text-xs text-slate-500">{fmtDate(race.start_date, lang)} – {fmtDate(race.end_date, lang)} · {t("race_optional")}</span></span>
            </label>
          )}
          <div className="flex gap-3"><Opt v="own" label={t("own_boat")} />{e.charters?.length > 0 && <Opt v="charter" label={t("charter_boat")} />}</div>
          {f.boat_option === "own" ? (
            <Input placeholder={t("sail_number")} value={f.own_sail_number} onChange={(x) => setF({ ...f, own_sail_number: x.target.value })} data-testid="enroll-sail-number" />
          ) : (
            <div className="space-y-2">{e.charters.map((c) => (
              <label key={c.id} className={`flex cursor-pointer items-center justify-between rounded-2xl border p-3 ${f.charter_id === c.id ? "border-[#00A8B5] bg-cyan-50" : "border-slate-200"}`}>
                <span className="flex items-center gap-2"><input type="radio" checked={f.charter_id === c.id} onChange={() => setF({ ...f, charter_id: c.id })} data-testid={`enroll-charter-${c.id}`} />{c.model}</span>
                <span className="text-sm font-semibold">{eur(c.price)} {c.price_unit === "day" ? t("per_day") : ""}</span>
              </label>))}
            </div>
          )}
          {e.services?.length > 0 && <div className="space-y-2"><p className="text-sm font-semibold">{t("services_avail")}</p>{e.services.map((s) => (
            <label key={s.name} className="flex items-center gap-2 text-sm">
              <Checkbox checked={f.services.includes(s.name)} onCheckedChange={(v) => setF({ ...f, services: v ? [...f.services, s.name] : f.services.filter((x) => x !== s.name) })} data-testid={`enroll-service-${s.name}`} />{s.name} · {eur(s.price)}
            </label>))}</div>}
          <div className="space-y-1 rounded-2xl bg-[#0A2540] p-5 text-sm text-white" data-testid="enroll-summary">
            <p className="eyebrow mb-2">{t("summary")}</p>
            <p className="flex justify-between"><span>{t("clinic_fee")}</span><span>{eur(clinic)}</span></p>
            {ch && <p className="flex justify-between"><span>{t("charter")}</span><span>{eur(charter)}</span></p>}
            {services > 0 && <p className="flex justify-between"><span>{t("services_acc")}</span><span>{eur(services)}</span></p>}
            <p className="flex justify-between border-t border-white/20 pt-2 font-display text-lg font-bold"><span>{t("total")}</span><span data-testid="enroll-total">{eur(clinic + charter + services)}</span></p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <button onClick={() => submit(false)} disabled={busy || !f.participant_id || (evClasses.length > 0 && !f.class_name) || !datesOk} className="flex-1 rounded-full border-2 border-[#0A2540] px-5 py-3 text-sm font-semibold text-[#0A2540] transition-colors hover:bg-slate-50 disabled:opacity-60" data-testid="enroll-add-continue">{t("cart_add_continue")}</button>
            <button onClick={() => submit(true)} disabled={busy || !f.participant_id || (evClasses.length > 0 && !f.class_name) || !datesOk} className="btn-buoy flex-1 justify-center disabled:opacity-60" data-testid="enroll-confirm">{t("cart_add_go")}</button>
          </div>
        </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
